import { useQuery } from '@tanstack/react-query';

import { QueryKeys } from '@/constants/queryKeys';
import axios, { AxiosResponse } from 'axios';
import localConfig from '@/config';

export type RewardsToken = {
  reward_token_name: string;
  reward_token_amount: number;
  reward_token_address: string;
};

export type MerklCampaign = {
  campaign_id: string;
  campaign_type: number;
  pool_address: string;
  token_0_address: string;
  token_1_address: string;
  token_0_name: string;
  token_1_name: string;
  reward_tokens: Array<RewardsToken>;
  token_0_weight: number;
  token_1_weight: number;
  fees_weight: number;
  is_out_of_range_incentivized: boolean;
  apr: number;
  pool_tvl: number;
  card_name: string;
  start_date: string;
  end_date: string;
  hasMoreThanOneCampaign: boolean;
};

export type FormattedMerklCampaign = {
  campaign_id: string;
  campaign_type: number;
  pool_address: string;
  token_0_address: string;
  token_0_image?: string;
  token_1_address: string;
  token_1_image?: string;
  token_0_name: string;
  token_1_name: string;
  reward_tokens: Array<RewardsToken>;
  token_0_weight: number;
  token_1_weight: number;
  fees_weight: number;
  is_in_range: boolean;
  apr: number;
  pool_tvl: number;
  card_name: string;
  is_out_of_range_incentivized: boolean;
  start_date: string;
  end_date: string;
  campaign_url: string;
  hasMoreThanOneCampaign: boolean;
};

export type MerklDappCampaignsEntity = {
  dapp_merkl_id: string;
  reward_tokens: Array<RewardsToken>;
  reward_period_from: string;
  reward_period_to: string;
  merkl_campaigns: Array<MerklCampaign>;
};

const fetcher = async (dapp_merkl_id?: number | string) => {
  if (!dapp_merkl_id) return;

  try {
    const { data: merklDappsRewards } = await axios.get<void, AxiosResponse<MerklDappCampaignsEntity>>(
      `${localConfig.apiUrl}/merkl/dapps/${dapp_merkl_id}`
    );

    return merklDappsRewards;
  } catch (error) {
    console.error('error', error);

    return;
  }
};

export const useMerklDappsCampaigns = (dapp_merkl_id?: number | string, options = {}) => {
  return useQuery<MerklDappCampaignsEntity | undefined>({
    queryKey: [`${QueryKeys.MERKL_DAPPS_CAMPAIGNS}_${dapp_merkl_id}`],
    queryFn: async () => {
      return fetcher(dapp_merkl_id);
    },
    ...options,
  });
};
